import { Component } from "@theme/component";
import { preferencesProcessingAllowed, subscribeToPreferences } from "@theme/customer-privacy";
import { DialogCloseEvent, DialogOpenEvent } from "@theme/dialog";
import { MORPH_OPTIONS, morph } from "@theme/morph";
import { RecentlyViewed } from "@theme/recently-viewed-products";
import { scrollIntoView } from "@theme/scrolling";
import { sectionRenderer } from "@theme/section-renderer";
import { debounce, getSearchSubmissionTerm, onAnimationEnd, requestIdleCallback } from "@theme/utilities";

const ARIA_CURRENT = "aria-current";
const ARIA_HIDDEN = "aria-hidden";
const KEYBOARD_FOCUS_CLASS = "keyboard-focus";
const PRODUCTS_ROOT_SELECTOR = "#predictive-search-products";
const PRODUCTS_LIST_SELECTOR = ":scope > .predictive-search-results__wrapper-products";
const PRODUCTS_TITLE_SELECTOR = ":scope > .predictive-search-results__title";
const RECENTLY_VIEWED_WRAPPER_SELECTOR = ".recently-viewed-wrapper";
const RECENTLY_VIEWED_CARD_SELECTOR = ".recently-viewed-wrapper [data-product-id]";
const RECENTS_CLEARING_CLASS = "predictive-search-results__products--recents-clearing";
const RECENTS_CLEARED_CLASS = "predictive-search-results__products--recents-cleared";
const RESTORED_CARD_CLASS = "predictive-search-results__card--restored";

/**
 * Morph matches product cards by product, so a card node is never rewritten into another product.
 * @param {Node | undefined} node - The node morph is matching.
 * @returns {string | undefined} The card's product ID, or undefined to use morph's default key.
 */
const productCardKey = node =>
  node instanceof HTMLElement && node.matches(".predictive-search-results__card[data-product-id]")
    ? node.dataset.productId
    : undefined;

/**
 * Normalize a search term consistently for requests and stale-response checks.
 * @param {string} value - The value to normalize.
 * @returns {string} The normalized value.
 */
const normalizeSearchTerm = value => value.trim().replaceAll(/\s+/g, " ");

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ["searchInput", "predictiveSearchResults", "resetButton"];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Whether an empty-state render (the background merge or a customer reset) has settled while it
   * still owned an empty search. Until then every open queues the background merge again, so recently
   * viewed products still appear after a render was superseded, aborted by closing the dialog, or
   * skipped for a query the customer then abandoned.
   */
  #emptyStateLoaded = false;

  /**
   * The background recently viewed merge, from being queued until it settles.
   * @type {AbortController | null}
   */
  #recentsMerge = null;

  /**
   * Whether the results on screen answer a customer's query rather than showing the empty state.
   */
  #queryResultsShown = false;

  /**
   * The empty state before recently viewed products are merged into it.
   * Retained so clearing history can restore default recommendations without another request.
   * @type {string | null}
   */
  #defaultEmptyStateMarkup = null;

  #preferencesRevision = 0;
  /** @type {boolean | null | undefined} */
  #preferencePermission;

  /** @param {boolean | null} allowed - Effective native preference permission. */
  #handlePreferencesChange = allowed => {
    const previous = this.#preferencePermission;
    this.#preferencePermission = allowed;
    if (previous === allowed || (previous === undefined && allowed === true)) {
      return;
    }

    this.#emptyStateLoaded = false;
    if (allowed === true) {
      if (RecentlyViewed.getProducts().length > 0) {
        this.#loadEmptyState();
      }
      return;
    }

    this.#preferencesRevision += 1;
    this.#cancelRecentsMerge();
    if (!this.#hasCustomerQuery()) {
      this.#activeFetch?.abort();
      this.#activeFetch = null;
    }

    this.#finishRecentsHandoff();
    const recentGroups = this.refs.predictiveSearchResults.querySelectorAll(RECENTLY_VIEWED_WRAPPER_SELECTOR);
    if (recentGroups.length === 0) {
      return;
    }

    if ([...recentGroups].some(group => group.contains(document.activeElement))) {
      this.refs.searchInput.focus({ preventScroll: true });
    }
    this.#currentIndex = -1;

    const currentProducts = this.refs.predictiveSearchResults.querySelector(PRODUCTS_ROOT_SELECTOR);
    const defaultProducts = this.#defaultEmptyStateMarkup
      ? new DOMParser()
          .parseFromString(this.#defaultEmptyStateMarkup, "text/html")
          .querySelector(PRODUCTS_ROOT_SELECTOR)
      : null;
    if (currentProducts instanceof HTMLElement && defaultProducts instanceof HTMLElement) {
      this.#reconcileDefaultProducts(currentProducts, defaultProducts);
    } else {
      for (const group of recentGroups) {
        group.remove();
      }
    }
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = event => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest("button") ||
      target.closest("a") ||
      target.closest("input");

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = event => {
    if (event.metaKey && event.key === "k") {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.search.cancel();
    this.resetSearch.cancel();
    this.#cancelRecentsMerge();
    this.#finishRecentsHandoff();

    // An empty search with no query results on screen is already showing its default state. Avoid an
    // unnecessary close-time fetch/morph that could finish after a quick reopen and replace a link
    // mid-click. An empty-state render aborted here has not set #emptyStateLoaded, so the next open
    // merges recently viewed products again. Query results under an emptied input (Escape clears the
    // input, then closes the dialog) still need the reset below, or they reappear on the next open.
    if (normalizeSearchTerm(this.refs.searchInput.value).length === 0 && !this.#queryResultsShown) {
      this.#activeFetch?.abort();
      this.#activeFetch = null;
      this.#currentIndex = -1;
      this.refs.searchInput.value = "";
      this.#hideResetButton();
      return;
    }

    // This customer reset clears any query results and fetches recently viewed products itself.
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  /** @param {FormDataEvent} event */
  #handleFormData = event => {
    event.formData.set("q", getSearchSubmissionTerm(this.refs.searchInput.value));
  };

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;
  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = event => {
    if (event.key === "Escape") {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === "ArrowLeft" || event.key === "ArrowRight") {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case "ArrowDown": {
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;
      }

      case "ArrowUp": {
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;
      }

      case "Enter": {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector("[data-single-result-url]");
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          globalThis.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector("a")?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set("q", getSearchSubmissionTerm(this.refs.searchInput.value));
          searchUrl.searchParams.set("type", "product");
          searchUrl.searchParams.set("options[prefix]", "last");
          globalThis.location.href = searchUrl.toString();
        }
        break;
      }

      default: {
        break;
      }
    }
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce(event => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) {
      return;
    }

    const searchTerm = normalizeSearchTerm(this.refs.searchInput.value);
    this.#currentIndex = -1;

    if (searchTerm.length === 0) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Customer reset (clear button, Escape, emptied input, closing with a query): clear the input and
   * the keyboard selection, then show the empty state.
   */
  #resetSearch = async () => {
    this.#finishRecentsHandoff();
    this.#currentIndex = -1;
    this.refs.searchInput.value = "";
    this.#hideResetButton();

    await this.#renderEmptyState(this.#createAbortController());
  };

  /**
   * Background merge of recently viewed products into the empty state (idle time or first open).
   * Unlike a customer reset it never changes the input value, focus, or a customer's query results.
   * While the customer has a query it does nothing: clearing that query is a customer reset, which
   * fetches recently viewed products itself (if that reset never settles, the next open merges again).
   */
  #mergeRecentlyViewed = debounce(
    /** @param {AbortController} recentsMerge - The controller #loadEmptyState queued this merge with. */
    async recentsMerge => {
      try {
        // #emptyStateLoaded: a customer reset settled while this merge was queued.
        if (recentsMerge.signal.aborted || !this.isConnected || this.#emptyStateLoaded || this.#hasCustomerQuery()) {
          return;
        }

        await this.#renderEmptyState(this.#createAbortController(recentsMerge));
      } finally {
        if (this.#recentsMerge === recentsMerge) {
          this.#recentsMerge = null;
        }
      }
    },
    100,
  );

  /**
   * Show the empty state for a request. Once it settles while it still owns an empty search (committed,
   * or the markup cannot hold recently viewed products), later opens need not merge again. A render
   * that is superseded, aborted, or fails leaves the merge to the next open.
   * @param {AbortController} abortController - This request's controller from #createAbortController.
   */
  async #renderEmptyState(abortController) {
    await this.#morphEmptyState(abortController);
    if (this.#isCurrentReset(abortController)) {
      this.#emptyStateLoaded = true;
    }
  }

  /**
   * Fetch the default empty state, merge recently viewed products ahead of it, and morph it in while
   * the request still owns an empty search. It never changes the input value. It moves focus only to
   * keep it on a product whose focused card the merge removes or moves, and keeps the keyboard
   * selection on the same product.
   * @param {AbortController} abortController - This request's controller from #createAbortController.
   */
  async #morphEmptyState(abortController) {
    const preferencesRevision = this.#preferencesRevision;
    const emptySectionId = "predictive-search-empty";
    const url = new URL(globalThis.location.href);
    url.searchParams.delete("page");

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    if (!this.#isCurrentReset(abortController)) {
      return;
    }

    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, "text/html")
      .querySelector(".predictive-search-empty-section");

    if (!parsedEmptySectionMarkup) {
      console.error("predictive-search: empty section markup not found in response");
      return;
    }

    this.#defaultEmptyStateMarkup = parsedEmptySectionMarkup.outerHTML;

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!this.#isCurrentReset(abortController)) {
        return;
      }

      // Consent changes discard optional history while the current reset still shows its defaults.
      if (
        recentlyViewedMarkup &&
        preferencesProcessingAllowed() === true &&
        preferencesRevision === this.#preferencesRevision
      ) {
        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, "text/html");
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.querySelector(PRODUCTS_ROOT_SELECTOR);
        const collectionElement = parsedEmptySectionMarkup.querySelector(PRODUCTS_ROOT_SELECTOR);
        if (recentlyViewedProductsHtml && collectionElement) {
          for (const child of recentlyViewedProductsHtml.children) {
            if (child instanceof HTMLElement) {
              child.setAttribute("ref", "recentlyViewedWrapper");
            }
          }
          collectionElement.prepend(...recentlyViewedProductsHtml.children);
          this.#deduplicateRecentlyViewedProducts(collectionElement);
        }
      }
    }

    if (!this.#isCurrentReset(abortController)) {
      return;
    }

    const selectedProductId = this.#currentItem?.dataset.productId;
    const focusedProductId = this.#removeCardsMovingToRecentlyViewed(parsedEmptySectionMarkup);
    this.#morphByProduct(parsedEmptySectionMarkup);
    this.#queryResultsShown = false;
    const selectedItem = this.#restoreKeyboardSelection(selectedProductId);
    this.#focusRecentlyViewedCard(focusedProductId);
    if (selectedItem) {
      // Keep the customer's selection in view, as arrowing to it did, instead of scrolling to the top.
      scrollIntoView(selectedItem, { block: "nearest" });
    } else {
      this.#resetScrollPositions();
    }
  }

  /**
   * Morph the results into `incoming`, matching product cards by product: the default list can differ
   * from the live one (the collection changed since the page loaded), and positional matching would
   * rewrite a card node, perhaps a focused or tapped link, into another tea. Morph moves a keyed card
   * by reinserting it, which drops focus from a link inside it, so focus returns to that link.
   * @param {Element} incoming - The detached markup to morph in.
   */
  #morphByProduct(incoming) {
    const focused = document.activeElement;
    morph(this.refs.predictiveSearchResults, incoming, { ...MORPH_OPTIONS, getNodeKey: productCardKey });
    if (focused instanceof HTMLElement && focused !== document.activeElement && focused.isConnected) {
      focused.focus({ preventScroll: true });
    }
  }

  /**
   * Morph copies the incoming attributes onto every card it keeps, which drops the keyboard selection
   * (aria-current) a customer made while the empty state was loading, so Enter would open the search
   * page instead of that product. Put the selection back on the same product, which may have moved
   * into Recently viewed, without the #currentIndex setter: it would move focus to the input.
   * @param {string | undefined} productId - The product whose card held the selection, if any.
   * @returns {HTMLElement | undefined} The card that holds the selection again.
   */
  #restoreKeyboardSelection(productId) {
    const selectedItem = productId
      ? this.#allResultsItems.find(item => item.dataset.productId === productId)
      : undefined;
    if (selectedItem) {
      selectedItem.setAttribute(ARIA_CURRENT, "true");
      selectedItem.classList.toggle(KEYBOARD_FOCUS_CLASS, this.#isKeyboardNavigation);
    }
    return selectedItem;
  }

  /**
   * A live default card whose product moves into Recently viewed leaves the default list, so morph would
   * move every following card to close the gap (reinserting a card replays its entrance animation and
   * drops focus from its link). Removing exactly those live cards first leaves every remaining card in
   * place with its node, product, and focus.
   * @param {Element} incoming - The detached markup about to be morphed in.
   * @returns {string | undefined} The product ID of a removed card that contained focus.
   */
  #removeCardsMovingToRecentlyViewed(incoming) {
    const movingProductIds = new Set(
      [...incoming.querySelectorAll(RECENTLY_VIEWED_CARD_SELECTOR)]
        .map(card => (card instanceof HTMLElement ? card.dataset.productId : undefined))
        .filter(Boolean),
    );
    /** @type {string | undefined} */
    let focusedProductId;
    if (movingProductIds.size === 0) {
      return focusedProductId;
    }

    const liveProducts = this.refs.predictiveSearchResults.querySelector(PRODUCTS_ROOT_SELECTOR);
    const liveDefaultList = liveProducts?.querySelector(PRODUCTS_LIST_SELECTOR);
    for (const card of liveDefaultList?.querySelectorAll(":scope > [data-product-id]") ?? []) {
      if (card instanceof HTMLElement && movingProductIds.has(card.dataset.productId)) {
        if (card.contains(document.activeElement)) {
          focusedProductId = card.dataset.productId;
        }
        card.remove();
      }
    }
    return focusedProductId;
  }

  /**
   * The background merge leaves focus where the customer put it, but removing a focused default card
   * (its product moved into Recently viewed) would drop focus to the page. Keep it on that product.
   * @param {string | undefined} productId - The product whose focused card was removed, if any.
   */
  #focusRecentlyViewedCard(productId) {
    if (!productId) {
      return;
    }

    const recentCard = [...this.refs.predictiveSearchResults.querySelectorAll(RECENTLY_VIEWED_CARD_SELECTOR)].find(
      card => card instanceof HTMLElement && card.dataset.productId === productId,
    );
    const recentLink = recentCard?.querySelector("a");
    (recentLink instanceof HTMLElement ? recentLink : this.refs.searchInput).focus();
  }

  /**
   * Get the dialog component.
   * @returns {import('./dialog.js').DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest("dialog-component");
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    const { dialog } = this;
    const { signal } = this.#controller;

    subscribeToPreferences(this.#handlePreferencesChange, { signal });

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.refs.searchInput.form?.addEventListener("formdata", this.#handleFormData, { signal });

    if (dialog) {
      document.addEventListener("keydown", this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      // Not `once`: each open queues the recently viewed merge again until an empty-state render settles.
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener("click", this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#activeFetch?.abort();
    this.search.cancel();
    this.resetSearch.cancel();
    this.#cancelRecentsMerge();
  }

  #loadEmptyState() {
    if (this.#emptyStateLoaded || this.#recentsMerge) {
      return;
    }
    const recentsMerge = new AbortController();
    this.#recentsMerge = recentsMerge;
    this.#mergeRecentlyViewed(recentsMerge);
  }

  /**
   * Cancel a queued background merge and abort one in flight.
   */
  #cancelRecentsMerge() {
    const recentsMerge = this.#recentsMerge;
    if (!recentsMerge) {
      return;
    }

    this.#recentsMerge = null;
    this.#mergeRecentlyViewed.cancel();
    recentsMerge.abort();
  }

  /**
   * Whether the results belong to a customer's query: text in the input (its search may be queued or in
   * flight) or query results still on screen.
   * @returns {boolean}
   */
  #hasCustomerQuery() {
    return normalizeSearchTerm(this.refs.searchInput.value).length > 0 || this.#queryResultsShown;
  }

  get #allResultsItems() {
    const containers = [
      ...this.querySelectorAll(".predictive-search-results__wrapper-products, .predictive-search-results__list"),
    ];

    return /** @type {HTMLElement[]} */ (
      containers
        .flatMap(container => {
          if (container.classList.contains("predictive-search-results__wrapper-products")) {
            return [...container.querySelectorAll(".predictive-search-results__card")];
          }
          return [...container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card')];
        })
        .filter(
          item => item instanceof HTMLElement && !item.closest('[aria-hidden="true"]') && !item.closest("[inert]"),
        )
    );
  }

  get #currentIndex() {
    return this.#allResultsItems?.findIndex(item => item.getAttribute(ARIA_CURRENT) === "true") ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) {
      return;
    }

    let activeItem = null;

    this.#allResultsItems.forEach(item => {
      item.classList.remove(KEYBOARD_FOCUS_CLASS);
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute(ARIA_CURRENT, "true");
        if (this.#isKeyboardNavigation) {
          item.classList.add(KEYBOARD_FOCUS_CLASS);
        }
        activeItem = item;
      } else {
        item.removeAttribute(ARIA_CURRENT);
      }
    }

    // Keyboard roving marks the active result with aria-current (valid on any element) and keeps it
    // in view; DOM focus stays in the input. We deliberately do NOT use the combobox
    // aria-activedescendant model — the results are not an ARIA listbox/option set (the result cards
    // are interactive, which `option` may not contain). Result counts are announced via the
    // role="status" live region in the results markup.
    const input = /** @type {HTMLInputElement} */ (this.refs.searchInput);
    if (activeItem) {
      scrollIntoView(activeItem, { block: "nearest" });
    }
    input.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  async clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      this.refs.searchInput.focus();

      if (await this.#restoreDefaultEmptyState()) {
        return;
      }

      recentlyViewedWrapper.classList.add("removing");
      await onAnimationEnd(recentlyViewedWrapper);

      if (!recentlyViewedWrapper.isConnected) {
        return;
      }

      recentlyViewedWrapper.remove();
    }
  }

  /**
   * Restore cached default recommendations while preserving every product card already on screen.
   * A partial recent group collapses first; a fully overlapping group is reconciled in place so the
   * search surface never passes through an empty state.
   * @returns {Promise<boolean>} Whether the default empty state was restored.
   */
  async #restoreDefaultEmptyState() {
    if (
      !this.#defaultEmptyStateMarkup ||
      !this.isConnected ||
      normalizeSearchTerm(this.refs.searchInput.value).length > 0
    ) {
      return false;
    }

    const parsedDefaultEmptyState = new DOMParser().parseFromString(this.#defaultEmptyStateMarkup, "text/html");
    const defaultProducts = parsedDefaultEmptyState.querySelector(PRODUCTS_ROOT_SELECTOR);
    const currentProducts = this.refs.predictiveSearchResults.querySelector(PRODUCTS_ROOT_SELECTOR);
    const recentlyViewedWrapper = currentProducts?.querySelector(":scope > .recently-viewed-wrapper");
    const currentDefaultProducts = currentProducts?.querySelector(PRODUCTS_LIST_SELECTOR);

    if (
      !(defaultProducts instanceof HTMLElement) ||
      !(currentProducts instanceof HTMLElement) ||
      !(recentlyViewedWrapper instanceof HTMLElement) ||
      !defaultProducts.querySelector(PRODUCTS_TITLE_SELECTOR) ||
      !defaultProducts.querySelector(PRODUCTS_LIST_SELECTOR)
    ) {
      return false;
    }

    this.#currentIndex = -1;
    recentlyViewedWrapper.setAttribute(ARIA_HIDDEN, "true");
    recentlyViewedWrapper.inert = true;
    currentProducts.classList.add(RECENTS_CLEARING_CLASS);

    // If recents cover every default, the cards already occupy the correct visual region. Reuse
    // them immediately instead of collapsing the panel and cloning the same cards back into it.
    if (!(currentDefaultProducts instanceof HTMLElement)) {
      this.#reconcileDefaultProducts(currentProducts, defaultProducts);
      this.#resetScrollPositions();
      return true;
    }

    const defaultProductsList = defaultProducts.querySelector(PRODUCTS_LIST_SELECTOR);
    if (defaultProductsList instanceof HTMLElement) {
      await this.#setRecentsCollapseFloor(recentlyViewedWrapper, currentDefaultProducts, defaultProductsList);
    }

    // A query, reset, or dialog close may have settled this state while the hidden measurement
    // was being committed.
    if (!currentProducts.isConnected || !currentProducts.classList.contains(RECENTS_CLEARING_CLASS)) {
      return true;
    }

    recentlyViewedWrapper.classList.add("removing");

    // Let the transition enter the document timeline before getAnimations() inspects it. Calling
    // getAnimations() in the same task as the class mutation forces style and layout synchronously.
    await new Promise(resolve => requestAnimationFrame(resolve));
    await onAnimationEnd(recentlyViewedWrapper);

    // A query, reset, or dialog close may already have settled or replaced this state.
    if (!currentProducts.isConnected || !currentProducts.classList.contains(RECENTS_CLEARING_CLASS)) {
      return true;
    }

    this.#reconcileDefaultProducts(currentProducts, defaultProducts);
    this.#resetScrollPositions();
    return true;
  }

  /**
   * Reserve any height the complete default grid gains when its missing cards return. The invisible
   * measurement is staged across rendering frames so both geometry reads use committed layout
   * instead of forcing it in the Clear click task. It is removed before the visible collapse begins.
   * @param {HTMLElement} recentlyViewedWrapper - Recent group being collapsed.
   * @param {HTMLElement} currentList - Visible deduplicated default list.
   * @param {HTMLElement} defaultList - Detached complete default list.
   * @returns {Promise<void>}
   */
  async #setRecentsCollapseFloor(recentlyViewedWrapper, currentList, defaultList) {
    const measurementList = /** @type {HTMLElement} */ (defaultList.cloneNode(true));

    await new Promise(resolve => requestAnimationFrame(resolve));
    const currentContainer = currentList.closest(".predictive-search-results__inner");
    if (!currentList.isConnected || !recentlyViewedWrapper.isConnected || !(currentContainer instanceof HTMLElement)) {
      return;
    }

    const currentListRect = currentList.getBoundingClientRect();
    const currentContainerInlineSize = currentContainer.clientWidth;
    const recentlyViewedRect = recentlyViewedWrapper.getBoundingClientRect();
    const measurementHost = document.createElement("div");
    measurementHost.className = "search-modal predictive-search-dropdown predictive-search-results__measurement-host";
    measurementHost.setAttribute(ARIA_HIDDEN, "true");
    measurementHost.inert = true;
    measurementHost.style.setProperty(
      "--predictive-search-measurement-container-inline-size",
      `${currentContainerInlineSize}px`,
    );
    measurementList.classList.add("predictive-search-results__wrapper-products--measurement");
    measurementList.setAttribute(ARIA_HIDDEN, "true");
    measurementList.inert = true;
    measurementList.style.setProperty("--predictive-search-measurement-inline-size", `${currentListRect.width}px`);
    recentlyViewedWrapper.style.setProperty(
      "--predictive-search-recents-start-height",
      `${recentlyViewedRect.height}px`,
    );

    // The measurement clone is deliberately invisible to component refs, accessibility, tests,
    // and product-card identity while it briefly participates in layout.
    for (const element of [measurementList, ...measurementList.querySelectorAll("*")]) {
      element.removeAttribute("id");
      element.removeAttribute("ref");
      if (element instanceof HTMLElement) {
        delete element.dataset.productId;
        delete element.dataset.testid;
      }
    }

    measurementHost.append(measurementList);
    document.body.append(measurementHost);
    await new Promise(resolve => requestAnimationFrame(resolve));

    if (!measurementHost.isConnected || !currentList.isConnected || !recentlyViewedWrapper.isConnected) {
      measurementHost.remove();
      return;
    }

    const collapseFloor = Math.max(0, measurementList.getBoundingClientRect().height - currentListRect.height);
    measurementHost.remove();
    recentlyViewedWrapper.style.setProperty("--predictive-search-recents-collapse-floor", `${collapseFloor}px`);
  }

  /**
   * Reconcile the cached default heading/list using the live card nodes wherever product IDs match.
   * Existing default cards remain untouched; cards moving out of Recents receive the only entrance
   * animation in a partial handoff.
   * @param {HTMLElement} currentProducts - Live products root.
   * @param {HTMLElement} defaultProducts - Detached cached default products root.
   * @returns {boolean} Whether reconciliation completed.
   */
  #reconcileDefaultProducts(currentProducts, defaultProducts) {
    const defaultTitle = defaultProducts.querySelector(PRODUCTS_TITLE_SELECTOR);
    const defaultList = defaultProducts.querySelector(PRODUCTS_LIST_SELECTOR);
    const currentTitle = currentProducts.querySelector(PRODUCTS_TITLE_SELECTOR);
    const currentList = currentProducts.querySelector(PRODUCTS_LIST_SELECTOR);
    const recentlyViewedWrapper = currentProducts.querySelector(":scope > .recently-viewed-wrapper");

    if (!(defaultTitle instanceof HTMLElement) || !(defaultList instanceof HTMLElement)) {
      return false;
    }

    /** @type {Map<string, HTMLElement>} */
    const currentCardsById = new Map();
    for (const card of currentProducts.querySelectorAll(".predictive-search-results__card--product[data-product-id]")) {
      if (card instanceof HTMLElement && card.dataset.productId && !currentCardsById.has(card.dataset.productId)) {
        currentCardsById.set(card.dataset.productId, card);
      }
    }

    const finalList =
      currentList instanceof HTMLElement ? currentList : /** @type {HTMLElement} */ (defaultList.cloneNode(false));
    /** @type {HTMLElement[]} */
    const finalCards = [];

    for (const defaultCard of defaultList.querySelectorAll(
      ":scope > .predictive-search-results__card--product[data-product-id]",
    )) {
      if (defaultCard instanceof HTMLElement && defaultCard.dataset.productId) {
        const existingCard = currentCardsById.get(defaultCard.dataset.productId);
        const wasRecentlyViewed = Boolean(existingCard?.closest(RECENTLY_VIEWED_WRAPPER_SELECTOR));
        const finalCard = existingCard ?? defaultCard;

        finalCard.setAttribute("ref", "resultsItems[]");
        finalCard.removeAttribute(ARIA_CURRENT);
        finalCard.classList.remove(KEYBOARD_FOCUS_CLASS);
        finalCard.classList.toggle(
          RESTORED_CARD_CLASS,
          !existingCard || (currentList instanceof HTMLElement && wasRecentlyViewed),
        );
        finalCards.push(finalCard);
      }
    }

    finalList.replaceChildren(...finalCards);

    if (currentTitle instanceof HTMLElement && currentList instanceof HTMLElement) {
      recentlyViewedWrapper?.remove();
    } else {
      currentProducts.replaceChildren(defaultTitle, finalList);
    }

    currentProducts.classList.remove(RECENTS_CLEARING_CLASS);
    currentProducts.classList.add(RECENTS_CLEARED_CLASS);
    return true;
  }

  /**
   * Settle any interrupted recently-viewed handoff before another results state is applied.
   */
  #finishRecentsHandoff() {
    const { predictiveSearchResults } = this.refs;
    const currentProducts = predictiveSearchResults.querySelector(`.${RECENTS_CLEARING_CLASS}`);
    if (!(currentProducts instanceof HTMLElement) || !this.#defaultEmptyStateMarkup) {
      return;
    }

    const defaultProducts = new DOMParser()
      .parseFromString(this.#defaultEmptyStateMarkup, "text/html")
      .querySelector(PRODUCTS_ROOT_SELECTOR);

    if (defaultProducts instanceof HTMLElement) {
      this.#reconcileDefaultProducts(currentProducts, defaultProducts);
    }
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector(".predictive-search-results__inner")?.scrollTo(0, 0);
      this.querySelector(".predictive-search-form__content")?.scrollTo(0, 0);
    });
  }

  /**
   * Check that a response still belongs to the value currently in the search input.
   * @param {string} searchTerm - The normalized term used for the request.
   * @returns {boolean} Whether the response is still current.
   */
  #isCurrentSearchTerm(searchTerm) {
    return normalizeSearchTerm(this.refs.searchInput.value) === searchTerm;
  }

  /**
   * Check that an empty-state response is still current.
   * @param {AbortController} abortController - The controller associated with the reset.
   * @returns {boolean} Whether the reset may still update the DOM.
   */
  #isCurrentReset(abortController) {
    return !abortController.signal.aborted && normalizeSearchTerm(this.refs.searchInput.value).length === 0;
  }

  /**
   * Remove default-state product cards duplicated by the recently viewed group,
   * then omit the default group when no unique products remain.
   * This operates on detached response markup before it is morphed into the page.
   * @param {Element} collectionElement - The detached products container.
   */
  #deduplicateRecentlyViewedProducts(collectionElement) {
    const recentlyViewedProductIds = new Set(
      [...collectionElement.querySelectorAll(RECENTLY_VIEWED_CARD_SELECTOR)]
        .map(product => (product instanceof HTMLElement ? product.dataset.productId : undefined))
        .filter(Boolean),
    );

    if (recentlyViewedProductIds.size === 0) {
      return;
    }

    for (const product of collectionElement.querySelectorAll(
      ".predictive-search-results__card--product[data-product-id]",
    )) {
      const productId = product instanceof HTMLElement ? product.dataset.productId : undefined;
      if (productId && recentlyViewedProductIds.has(productId) && !product.closest(RECENTLY_VIEWED_WRAPPER_SELECTOR)) {
        product.remove();
      }
    }

    const defaultProductsList = collectionElement.querySelector(PRODUCTS_LIST_SELECTOR);
    if (!defaultProductsList || defaultProductsList.querySelector(".predictive-search-results__card--product")) {
      return;
    }

    const defaultProductsTitle = defaultProductsList.previousElementSibling;
    if (defaultProductsTitle?.matches(".predictive-search-results__title")) {
      defaultProductsTitle.remove();
    }
    defaultProductsList.remove();
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) {
      return;
    }

    // Use the search page's engine and query rules so suggestions cannot broaden
    // into unrelated teas or omit matches from product descriptions and tags.
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set("q", getSearchSubmissionTerm(searchTerm));
    url.searchParams.set("type", "product");
    url.searchParams.set("options[prefix]", "last");

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);

      if (!resultsMarkup) {
        return;
      }

      if (abortController.signal.aborted || !this.#isCurrentSearchTerm(searchTerm)) {
        return;
      }

      // Predictive search always renders inside the modal dialog. A document-level
      // View Transition can briefly paint above that top layer and expose the canvas
      // behind the scroll-locked, negatively offset body. Morph the results directly;
      // the result cards retain their existing component-local entrance treatment.
      this.#finishRecentsHandoff();
      morph(predictiveSearchResults, resultsMarkup);
      this.#resetScrollPositions();
      this.#queryResultsShown = true;
    } catch (error) {
      if (abortController.signal.aborted || !this.#isCurrentSearchTerm(searchTerm)) {
        return;
      }
      throw error;
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string> | null} The markup for the recently viewed products.
   */
  #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) {
      return null;
    }

    const viewedProducts = RecentlyViewed.getEntries();
    if (viewedProducts.length === 0) {
      return null;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set("q", viewedProducts.map(product => product.handle).join(" OR "));
    url.searchParams.set("type", "product");

    return sectionRenderer.getSectionHTML("predictive-search-recently-viewed", false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  /**
   * Make a request the live owner of the results, aborting the request it supersedes.
   * @param {AbortController} [abortController] - The new owner's controller.
   * @returns {AbortController} The live controller.
   */
  #createAbortController(abortController = new AbortController()) {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }
}

if (!customElements.get("predictive-search-component")) {
  customElements.define("predictive-search-component", PredictiveSearchComponent);
}
